import { useState, type ChangeEvent, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TEST_USER_CSV_COLUMNS, TesterRole, type TestUser, type TestUserCreate, type TestUserImportResult } from '@regigov/contracts';
import { ApiError } from '../api/client';
import { participantsApi } from '../api/participants';
import { testingApi } from '../api/testing';
import { Field, ProblemAlert, StatusBadge, formatDateTime } from './ui';

const ROLE_LABELS: Record<TesterRole, string> = {
  cer_admin: 'CER admin',
  cer_officer: 'CER officer',
  participant_admin: 'Participant admin',
  participant_user: 'Participant user',
  applicant: 'Applicant',
};

const CSV_TEMPLATE = `${TEST_USER_CSV_COLUMNS.join(',')}\njane.tester,Jane Tester,jane.tester@example.com,cer_officer,,true\nsam.installer,Sam Installer,sam@example.com,participant_user,CER-P-01J000000000000000GRIDCELL,false\n`;

/** Test admin page (TESTING_MODE=true): every account the Testing portal created or imported, with its current roles and organisation. */
export function TestingPage() {
  const users = useQuery({ queryKey: ['testing', 'users'], queryFn: testingApi.users });

  if (users.error instanceof ApiError && (users.error.status === 404 || users.error.status === 403)) {
    return (
      <>
        <h1>Testing</h1>
        <p className="muted">
          {users.error.status === 404
            ? 'Testing mode is turned off. Set TESTING_MODE=true on the API to use the Testing portal.'
            : 'Only the test admin can use the Testing portal.'}
        </p>
      </>
    );
  }

  return (
    <>
      <h1>Testing</h1>
      <p className="muted">Accounts added through the Testing portal, with their current roles and organisation.</p>
      <AddUserForm />
      <CsvUpload />
      <ExportImport />
      <h2>Test users</h2>
      <ProblemAlert error={users.error} />
      {users.isLoading && <p className="muted">Loading…</p>}
      {users.data && users.data.items.length === 0 && <p className="muted">No test users yet.</p>}
      {users.data && users.data.items.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Name</th>
              <th>Email</th>
              <th>Tester</th>
              <th>Roles</th>
              <th>Organisation</th>
              <th>Status</th>
              <th>Added</th>
              <th>Invitation</th>
            </tr>
          </thead>
          <tbody>
            {users.data.items.map((u) => (
              <TestUserRow key={u.id} user={u} />
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function TestUserRow({ user: u }: { user: TestUser }) {
  const resend = useMutation({ mutationFn: () => testingApi.resendInvitation(u.id) });
  return (
    <tr>
      <td>{u.username}</td>
      <td>{u.displayName}</td>
      <td>
        {u.email ?? <span className="muted">None</span>}
        {u.email && !u.emailVerified && <span className="muted"> (not verified)</span>}
      </td>
      <td>{u.tester ? 'Yes' : 'No'}</td>
      <td>{u.roles.join(', ')}</td>
      <td>{u.organisation ? `${u.organisation.legalName} (ABN ${u.organisation.abn})` : <span className="muted">None</span>}</td>
      <td>
        <StatusBadge status={u.disabled ? 'disabled' : 'active'} />
      </td>
      <td>{formatDateTime(u.onboardedAt)}</td>
      <td>
        {u.email && !u.disabled ? (
          <>
            <button type="button" className="secondary" disabled={resend.isPending} onClick={() => resend.mutate()}>
              {resend.isPending ? 'Sending…' : 'Resend invitation'}
            </button>
            {resend.isSuccess && <span className="notice"> Sent</span>}
            <ProblemAlert error={resend.error} />
          </>
        ) : (
          <span className="muted">No email</span>
        )}
      </td>
    </tr>
  );
}

const EMPTY = { username: '', displayName: '', email: '', role: 'cer_officer' as TesterRole, participantId: '', tester: true, sendInvitation: true, password: '' };

function AddUserForm() {
  const qc = useQueryClient();
  const [v, setV] = useState(EMPTY);
  const [added, setAdded] = useState<string | null>(null);
  const participantRole = v.role === 'participant_admin' || v.role === 'participant_user';
  const [orgFilter, setOrgFilter] = useState('');
  const orgs = useQuery({ queryKey: ['participants', 'approved', orgFilter], queryFn: () => participantsApi.search(orgFilter.trim(), 'approved'), enabled: participantRole });
  // A chosen organisation the search has filtered out of the list no longer counts, so it can't be added to unseen.
  const chosenVisible = (orgs.data?.items ?? []).some((o) => o.id === v.participantId);
  const create = useMutation({
    mutationFn: (body: TestUserCreate) => testingApi.createUser(body),
    onSuccess: (u) => {
      setAdded(`Added ${u.username}${v.sendInvitation ? '; invitation queued' : ''}`);
      setV(EMPTY);
      void qc.invalidateQueries({ queryKey: ['testing', 'users'] });
    },
  });
  const set = (k: keyof typeof EMPTY) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const value = e.target instanceof HTMLInputElement && e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setV((old) => ({ ...old, [k]: value }));
  };

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setAdded(null);
    create.mutate({
      username: v.username,
      displayName: v.displayName,
      ...(v.email && { email: v.email }),
      role: v.role,
      ...(participantRole && chosenVisible && { participantId: v.participantId }),
      tester: v.tester,
      sendInvitation: v.sendInvitation,
      ...(!v.sendInvitation && v.password && { password: v.password }),
    });
  }

  return (
    <form className="form card" onSubmit={onSubmit} noValidate aria-labelledby="add-user-title">
      <h2 id="add-user-title">Add a user</h2>
      <div className="row">
        <Field label="Username">
          <input name="username" value={v.username} onChange={set('username')} required />
        </Field>
        <Field label="Display name">
          <input name="displayName" value={v.displayName} onChange={set('displayName')} required />
        </Field>
        <Field label="Email" hint={v.sendInvitation ? 'The invitation goes here' : 'Optional; recorded as verified'}>
          <input name="email" type="email" value={v.email} onChange={set('email')} />
        </Field>
      </div>
      <div className="row">
        <Field label="Role">
          <select name="role" value={v.role} onChange={set('role')}>
            {TesterRole.options.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABELS[r]}
              </option>
            ))}
          </select>
        </Field>
        {participantRole && (
          <Field label="Find organisation" hint="Searches approved participants; the list shows the first 50 matches">
            <input type="search" value={orgFilter} onChange={(e) => setOrgFilter(e.target.value)} placeholder="Name, ABN or ID" />
          </Field>
        )}
        {participantRole && (
          <Field label="Organisation" hint="Approved participants only">
            <select name="participantId" value={v.participantId} onChange={set('participantId')}>
              <option value="">Choose an organisation</option>
              {(orgs.data?.items ?? []).map((o) => (
                <option key={o.id} value={o.id}>
                  {o.legalName} ({o.abn})
                </option>
              ))}
            </select>
          </Field>
        )}
      </div>
      <label className="check">
        <input type="checkbox" name="tester" checked={v.tester} onChange={set('tester')} />
        Tester (can switch their own role and organisation)
      </label>
      <label className="check">
        <input type="checkbox" name="sendInvitation" checked={v.sendInvitation} onChange={set('sendInvitation')} />
        Send invitation email with a link to choose a password
      </label>
      {!v.sendInvitation && (
        <Field label="Initial password" hint="At least 12 characters. Tell the user yourself; it is never emailed">
          <input name="password" type="password" autoComplete="new-password" value={v.password} onChange={set('password')} />
        </Field>
      )}
      <ProblemAlert error={orgs.error ?? create.error} />
      {added && <p className="notice">{added}</p>}
      <div className="row">
        <button type="submit" disabled={create.isPending || (participantRole && !chosenVisible)}>
          {create.isPending ? 'Adding…' : 'Add user'}
        </button>
      </div>
    </form>
  );
}

function CsvUpload() {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const upload = useMutation({
    mutationFn: async () => testingApi.importCsv({ csv: await file!.text() }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['testing', 'users'] }),
  });

  return (
    <form
      className="form card"
      aria-labelledby="csv-title"
      onSubmit={(e) => {
        e.preventDefault();
        if (file) upload.mutate();
      }}
    >
      <h2 id="csv-title">Add users from a CSV</h2>
      <p className="muted">
        Columns: <code>{TEST_USER_CSV_COLUMNS.join(',')}</code>. Every row needs an email, and each user is sent an invitation to choose a password. <code>role</code> is one of {TesterRole.options.join(', ')};{' '}
        <code>organisation</code> is an approved participant's id or ABN, for participant roles only; <code>tester</code> is true or false. Every row is checked
        first, and nothing is added if any row has a problem.{' '}
        <a href={`data:text/csv;charset=utf-8,${encodeURIComponent(CSV_TEMPLATE)}`} download="regigov-test-users-template.csv">
          Download a template
        </a>
      </p>
      <Field label="CSV file">
        <input
          type="file"
          name="csv"
          accept=".csv,text/csv"
          onChange={(e) => {
            upload.reset();
            setFile(e.target.files?.[0] ?? null);
          }}
        />
      </Field>
      <ProblemAlert error={upload.error} />
      {upload.data && (
        <p className="notice">
          Added {upload.data.created.length} user{upload.data.created.length === 1 ? '' : 's'}
          ; invitations queued
        </p>
      )}
      <div className="row">
        <button type="submit" disabled={!file || upload.isPending}>
          {upload.isPending ? 'Uploading…' : 'Upload'}
        </button>
      </div>
    </form>
  );
}

/** T4: save every portal-added account (with password hashes) before a rebuild, and restore them afterwards. */
function ExportImport() {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const exporter = useMutation({
    mutationFn: testingApi.exportCsv,
    onSuccess: ({ blob, filename }) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    },
  });
  const preview = useMutation({ mutationFn: async () => testingApi.importUsers({ csv: await file!.text(), apply: false }) });
  const apply = useMutation({
    mutationFn: async () => testingApi.importUsers({ csv: await file!.text(), apply: true }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['testing', 'users'] }),
  });
  const plan: TestUserImportResult | undefined = apply.data ?? preview.data;

  return (
    <section className="form card" aria-labelledby="transfer-title">
      <h2 id="transfer-title">Export and import</h2>
      <p className="muted">
        Export saves every account listed below, with its password hash, so it can be imported after a full rebuild and its users sign in with their old
        passwords. The file contains password hashes: keep it private. Every imported user comes back as a CER officer with no organisation; the roles and
        organisation columns are for reference only. Delete rows you don't want before importing.
      </p>
      <div className="row">
        <button type="button" className="secondary" disabled={exporter.isPending} onClick={() => exporter.mutate()}>
          {exporter.isPending ? 'Exporting…' : 'Export test users (CSV)'}
        </button>
      </div>
      <ProblemAlert error={exporter.error} />
      <Field label="Export file to import">
        <input
          type="file"
          name="importFile"
          accept=".csv,text/csv"
          onChange={(e) => {
            preview.reset();
            apply.reset();
            setFile(e.target.files?.[0] ?? null);
          }}
        />
      </Field>
      <ProblemAlert error={preview.error ?? apply.error} />
      {plan && (
        <>
          <p className={apply.data ? 'notice' : 'muted'}>
            {apply.data
              ? `Imported: ${plan.creates} created, ${plan.updates} updated.`
              : `Preview: ${plan.creates} to create, ${plan.updates} to update. Nothing has changed yet.`}
          </p>
          <table className="table">
            <thead>
              <tr>
                <th>Line</th>
                <th>Username</th>
                <th>Name</th>
                <th>Email</th>
                <th>Tester</th>
                <th>Disabled</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {plan.rows.map((r) => (
                <tr key={r.line}>
                  <td>{r.line}</td>
                  <td>{r.username}</td>
                  <td>{r.displayName}</td>
                  <td>{r.email ?? <span className="muted">None</span>}</td>
                  <td>{r.tester ? 'Yes' : 'No'}</td>
                  <td>{r.disabled ? 'Yes' : 'No'}</td>
                  <td>{r.action}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      <div className="row">
        <button type="button" className="secondary" disabled={!file || preview.isPending || apply.isPending} onClick={() => preview.mutate()}>
          {preview.isPending ? 'Checking…' : 'Preview import'}
        </button>
        <button type="button" disabled={!preview.data || apply.isPending || apply.isSuccess} onClick={() => apply.mutate()}>
          {apply.isPending ? 'Importing…' : preview.data ? `Import ${preview.data.rows.length} user${preview.data.rows.length === 1 ? '' : 's'}` : 'Import'}
        </button>
      </div>
    </section>
  );
}
